import React from 'react';
import { ArrowRight, Lock, GitBranch, Plus, Clock, Sparkles, BadgeCheck } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import { MODULES, hasModuleAccess } from '../modules/registry';

const HomePage = ({ onNavigate, user, licenseState = null }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Kachel-Module aus dem Register — gefiltert nach tile_in_homepage.
  // enabled-Logik: Lizenz-Check (aeussere Schranke) + RBAC via hasModuleAccess.
  const registryTiles = MODULES
    .filter((m) => m.tile_in_homepage)
    .map((m) => {
      const accessible = hasModuleAccess(m, user, licenseState);
      return {
        id: m.id,
        name: t(m.tile.titleKey),
        description: t(m.tile.descKey),
        icon: m.tile.Icon,
        color: m.tile.color,
        badge: m.tile.badge || null,
        preview: false,
        enabled: accessible,
        onClick: accessible ? () => onNavigate(m.id) : null,
      };
    });

  // Preview-Kacheln (NICHT im Register): noch keine View dahinter, daher nicht
  // klickbar (enabled:false) — aber visuell AKTIV (preview:true) statt ausgegraut:
  // farbiges Icon, aktiver Titel, farbiger Status-Badge "In Vorbereitung".
  const placeholderTiles = [
    {
      id: 'workflow-editor',
      name: t('home.workflowEditor'),
      description: t('home.workflowEditorDesc'),
      icon: GitBranch,
      color: 'purple',
      badge: null,
      preview: true,
      enabled: false,
      onClick: null,
    },
    {
      id: 'coming-soon',
      name: t('home.moreModules'),
      description: t('home.moreModulesDesc'),
      icon: Plus,
      color: 'blue',
      badge: null,
      preview: true,
      dashed: true,
      enabled: false,
      onClick: null,
    },
  ];

  const modules = [...registryTiles, ...placeholderTiles];

  const getColorClasses = (color, styled) => {
    if (!styled) {
      return {
        cardBg: isDarkMode
          ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 border border-gray-600/30'
          : 'glass-light-card',
        iconBg: isDarkMode ? 'from-gray-500/40 via-gray-600/30 to-gray-700/40' : '',
        textColor: isDarkMode ? 'text-gray-500' : 'text-slate-400',
        hoverBg: ''
      };
    }

    switch (color) {
      case 'blue':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-blue-900/15 to-gray-900/30 border border-blue-400/25'
            : 'glass-light-card',
          cardHover: isDarkMode
            ? 'group-hover:from-gray-900/40 group-hover:via-blue-900/25 group-hover:to-gray-900/40 group-hover:border-blue-400/40'
            : '',
          iconBg: isDarkMode ? 'from-blue-500/70 via-blue-600/60 to-blue-700/70' : '',
          textColor: isDarkMode ? 'text-blue-300' : 'text-brand',
          hoverBg: isDarkMode
            ? 'bg-gradient-to-t from-blue-600/10 via-transparent to-blue-500/15'
            : ''
        };
      case 'emerald':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-emerald-900/15 to-gray-900/30 border border-emerald-400/25'
            : 'glass-light-card',
          cardHover: isDarkMode
            ? 'group-hover:from-gray-900/40 group-hover:via-emerald-900/25 group-hover:to-gray-900/40 group-hover:border-emerald-400/40'
            : '',
          iconBg: isDarkMode ? 'from-emerald-500/70 via-emerald-600/60 to-emerald-700/70' : '',
          textColor: isDarkMode ? 'text-emerald-300' : 'text-emerald-700',
          hoverBg: isDarkMode
            ? 'bg-gradient-to-t from-emerald-600/10 via-transparent to-emerald-500/15'
            : ''
        };
      case 'violet':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-violet-900/15 to-gray-900/30 border border-violet-400/25'
            : 'glass-light-card',
          cardHover: isDarkMode
            ? 'group-hover:from-gray-900/40 group-hover:via-violet-900/25 group-hover:to-gray-900/40 group-hover:border-violet-400/40'
            : '',
          iconBg: isDarkMode ? 'from-violet-500/70 via-violet-600/60 to-violet-700/70' : '',
          textColor: isDarkMode ? 'text-violet-300' : 'text-violet-600',
          hoverBg: isDarkMode
            ? 'bg-gradient-to-t from-violet-600/10 via-transparent to-violet-500/15'
            : ''
        };
      case 'amber':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-amber-900/15 to-gray-900/30 border border-amber-400/25'
            : 'glass-light-card',
          cardHover: isDarkMode
            ? 'group-hover:from-gray-900/40 group-hover:via-amber-900/25 group-hover:to-gray-900/40 group-hover:border-amber-400/40'
            : '',
          iconBg: isDarkMode ? 'from-amber-500/70 via-amber-600/60 to-amber-700/70' : '',
          textColor: isDarkMode ? 'text-amber-300' : 'text-amber-700',
          hoverBg: isDarkMode
            ? 'bg-gradient-to-t from-amber-600/10 via-transparent to-amber-500/15'
            : ''
        };
      case 'purple':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-purple-900/15 to-gray-900/30 border border-purple-400/25'
            : 'glass-light-card',
          cardHover: isDarkMode
            ? 'group-hover:from-gray-900/40 group-hover:via-purple-900/25 group-hover:to-gray-900/40 group-hover:border-purple-400/40'
            : '',
          iconBg: isDarkMode ? 'from-purple-500/70 via-purple-600/60 to-purple-700/70' : '',
          textColor: isDarkMode ? 'text-purple-300' : 'text-purple-600',
          hoverBg: isDarkMode
            ? 'bg-gradient-to-t from-purple-600/10 via-transparent to-purple-500/15'
            : ''
        };
      case 'indigo':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-indigo-900/15 to-gray-900/30 border border-indigo-400/25'
            : 'glass-light-card',
          cardHover: isDarkMode
            ? 'group-hover:from-gray-900/40 group-hover:via-indigo-900/25 group-hover:to-gray-900/40 group-hover:border-indigo-400/40'
            : '',
          iconBg: isDarkMode ? 'from-indigo-500/70 via-indigo-600/60 to-indigo-700/70' : '',
          textColor: isDarkMode ? 'text-indigo-300' : 'text-indigo-600',
          hoverBg: isDarkMode
            ? 'bg-gradient-to-t from-indigo-600/10 via-transparent to-indigo-500/15'
            : ''
        };
      case 'gray':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-gray-500/20 border-dashed'
            : 'glass-light-card border-dashed',
          cardHover: '',
          iconBg: isDarkMode ? 'from-gray-400/50 via-gray-500/40 to-gray-600/50' : '',
          textColor: isDarkMode ? 'text-gray-400' : 'text-slate-400',
          hoverBg: ''
        };
      default:
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20'
            : 'glass-light-card',
          iconBg: isDarkMode ? 'from-gray-500/60 via-gray-600/50 to-gray-700/60' : '',
          textColor: isDarkMode ? 'text-gray-300' : 'text-slate-500',
          hoverBg: ''
        };
    }
  };

  // Plaketten-Farben je tone: new = Brand/Blau, compliance = Grün, preview = Amber.
  const getBadgeClasses = (tone) => {
    switch (tone) {
      case 'compliance':
        return isDarkMode
          ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-400/30'
          : 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'preview':
        return isDarkMode
          ? 'bg-amber-500/15 text-amber-200 border border-amber-400/30'
          : 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'new':
      default:
        return isDarkMode
          ? 'bg-blue-500/15 text-blue-100 border border-blue-400/30'
          : 'bg-brand/10 text-brand border border-brand/20';
    }
  };

  return (
    <div className={`flex-1 ${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
      : 'bg-[#F8FAFC]'
    }`}>

      {/* Header Section */}
      <div className="relative">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
          : 'bg-white border-b border-slate-200'
        }`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-r from-blue-900/5 via-transparent to-purple-900/5'
          : ''
        }`}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className={`text-3xl font-bold mb-2 ${isDarkMode
              ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent drop-shadow-lg'
              : 'text-slate-900'
            }`}>
              {t('home.welcome', { name: user?.firstName || user?.username || t('home.userFallback') })}
            </h1>
            <p className={`text-lg ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
              {t('home.selectModule')}
            </p>
          </div>
        </div>
      </div>

      {/* Module Cards */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {modules.map((module) => {
            // styled = visuell aktiv (klickbar ODER Preview). Nur echt gesperrte
            // Kacheln (RBAC/Lizenz fehlt, kein Preview) werden ausgegraut.
            const styled = module.enabled || module.preview;
            const colors = getColorClasses(module.color, styled);
            const Icon = module.icon;

            // Light-Mode Icon-Hintergrund/-Farbe je Farb-Token (literal fuer Tailwind-Scan).
            const lightIconBg = ({ blue: 'bg-brand/10', violet: 'bg-violet-100', amber: 'bg-amber-100', emerald: 'bg-emerald-100', purple: 'bg-purple-100', indigo: 'bg-indigo-100' })[module.color] || 'bg-slate-100';
            const lightIconText = ({ blue: 'text-brand', violet: 'text-violet-600', amber: 'text-amber-600', emerald: 'text-emerald-600', purple: 'text-purple-600', indigo: 'text-indigo-600' })[module.color] || 'text-slate-400';

            // Plakette: nur Registry-Badge (z. B. E-Invoicing-Compliance).
            const pill = styled ? (module.badge || null) : null;

            return (
              <div
                key={module.id}
                onClick={module.enabled ? module.onClick : undefined}
                className={`relative group ${module.enabled ? 'cursor-pointer' : module.preview ? 'cursor-default' : 'cursor-not-allowed'} transition-all duration-300 ${module.enabled ? 'hover:scale-[1.02]' : ''}`}
              >
                {/* Card Background */}
                <div className={`absolute inset-0 ${colors.cardBg} ${module.dashed ? 'border-dashed' : ''} ${isDarkMode ? 'backdrop-blur-2xl shadow-lg' : ''} rounded-3xl ${module.enabled && isDarkMode ? 'group-hover:shadow-xl' : ''} transition-all duration-300`}></div>

                {/* Hover Overlay */}
                {module.enabled && isDarkMode && (
                  <div className={`absolute inset-0 ${colors.hoverBg} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                )}

                {/* Content */}
                <div className="relative p-8">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-5">
                      {/* Icon */}
                      <div className="relative">
                        {isDarkMode && <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm"></div>}
                        <div className={`relative w-16 h-16 rounded-2xl flex items-center justify-center ${isDarkMode
                          ? `bg-gradient-to-br ${colors.iconBg} backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20`
                          : styled ? lightIconBg : 'bg-slate-100'
                        }`}>
                          <Icon className={`w-8 h-8 ${isDarkMode
                            ? (styled ? 'text-white/90' : 'text-white/50') + ' drop-shadow-lg'
                            : styled ? lightIconText : 'text-slate-400'
                          }`} />
                          {!styled && (
                            <div className={`absolute -bottom-1 -right-1 rounded-full p-1 ${isDarkMode ? 'bg-gray-600' : 'bg-slate-300'}`}>
                              <Lock className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Text */}
                      <div className="flex-1">
                        <h2 className={`text-2xl font-bold mb-2 ${styled ? (isDarkMode ? 'text-white' : 'text-slate-900') : colors.textColor}`}>
                          {module.name}
                        </h2>
                        <p className={`${styled ? (isDarkMode ? 'text-white/70' : 'text-slate-500') : colors.textColor} text-sm leading-relaxed`}>
                          {module.description}
                        </p>
                        {/* Plakette: farbiger Badge (aktiv/Preview) ODER grauer Lock-Hinweis (gesperrt) */}
                        {pill ? (
                          <span className={`inline-flex items-center mt-3 px-3 py-1 rounded-full text-xs font-medium ${getBadgeClasses(pill.tone)}`}>
                            {pill.tone === 'compliance'
                              ? <BadgeCheck className="w-3.5 h-3.5 mr-1.5" />
                              : pill.tone === 'new'
                                ? <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                : <Clock className="w-3.5 h-3.5 mr-1.5" />}
                            {t(pill.labelKey)}
                          </span>
                        ) : !styled && (
                          <span className={`inline-flex items-center mt-3 px-3 py-1 rounded-full text-xs font-medium ${
                            isDarkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-slate-100 text-slate-500'
                          }`}>
                            <Lock className="w-3 h-3 mr-1.5" />
                            {t('home.comingSoon')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    {module.enabled && (
                      <div className={`flex-shrink-0 ml-4 p-3 rounded-xl ${
                        isDarkMode ? 'bg-white/5 group-hover:bg-white/10' : 'bg-slate-100 group-hover:bg-slate-200'
                      } transition-colors duration-300`}>
                        <ArrowRight className={`w-6 h-6 ${colors.textColor} group-hover:translate-x-1 transition-transform duration-300`} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default HomePage;
